import React from 'react';
import { Calendar, User, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mockBlogPosts } from '../mockData';
import { Link } from 'react-router-dom';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = React.useState('Tous');
  const categories = ['Tous', 'Innovation', 'Sécurité', 'Cloud', 'Infrastructure'];

  const filteredPosts = selectedCategory === 'Tous' 
    ? mockBlogPosts 
    : mockBlogPosts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 space-y-4 animate-in fade-in duration-700">
          <h1 className="text-5xl md:text-6xl font-bold text-white">
            Notre <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Blog</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Actualités, conseils et insights sur les dernières tendances technologiques
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg'
                  : 'bg-slate-900 text-gray-400 hover:text-white border border-slate-800 hover:border-teal-500/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Featured Post */}
        {filteredPosts.length > 0 && (
          <Card className="bg-slate-900 border-slate-800 mb-12 overflow-hidden hover:border-teal-500/50 transition-all duration-300 group cursor-pointer">
            <div className="grid md:grid-cols-2">
              <div className="relative h-64 md:h-auto overflow-hidden">
                <img 
                  src={filteredPosts[0].image} 
                  alt={filteredPosts[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Article Vedette
                  </span>
                </div>
              </div>
              <CardContent className="p-8 flex flex-col justify-center">
                <div className="flex items-center space-x-4 text-sm text-gray-400 mb-4">
                  <span className="flex items-center">
                    <Calendar className="mr-2" size={16} />
                    {new Date(filteredPosts[0].date).toLocaleDateString('fr-FR')}
                  </span>
                  <span className="flex items-center">
                    <Clock className="mr-2" size={16} />
                    {filteredPosts[0].readTime}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-4 group-hover:text-teal-400 transition-colors">
                  {filteredPosts[0].title}
                </h2>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {filteredPosts[0].excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <User className="text-teal-400" size={20} />
                    <span className="text-gray-300">{filteredPosts[0].author}</span>
                  </div>
                  <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white group/btn">
                    Lire l'article
                    <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                  </Button>
                </div>
              </CardContent>
            </div>
          </Card>
        )}

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.slice(1).map((post) => (
            <Card key={post.id} className="bg-slate-900 border-slate-800 overflow-hidden hover:border-teal-500/50 transition-all duration-300 group cursor-pointer hover:shadow-xl hover:shadow-teal-500/10">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-slate-900/80 backdrop-blur-sm text-teal-400 px-3 py-1 rounded-full text-xs font-medium">
                    {post.category}
                  </span>
                </div>
              </div>
              <CardContent className="p-6">
                <div className="flex items-center space-x-3 text-xs text-gray-400 mb-3">
                  <span className="flex items-center">
                    <Calendar className="mr-1" size={14} />
                    {new Date(post.date).toLocaleDateString('fr-FR')}
                  </span>
                  <span className="flex items-center">
                    <Clock className="mr-1" size={14} />
                    {post.readTime}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-teal-400 transition-colors line-clamp-2">
                  {post.title}
                </h3>
                <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                  <div className="flex items-center space-x-2">
                    <User className="text-teal-400" size={16} />
                    <span className="text-sm text-gray-300">{post.author}</span>
                  </div>
                  <Button variant="ghost" className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 p-2 group/btn">
                    <ArrowRight className="group-hover/btn:translate-x-1 transition-transform" size={16} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Newsletter CTA */}
        <section className="mt-20">
          <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/20">
            <CardContent className="p-12 text-center space-y-6">
              <h2 className="text-3xl font-bold text-white">
                Restez informé de nos dernières actualités
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Inscrivez-vous à notre newsletter pour recevoir nos articles, conseils et actualités directement dans votre boîte mail.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Votre adresse email"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-3">
                  S'inscrire
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Blog;